// src/pages/Expedientes/CertificadosTab.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Dialog, DialogTitle, DialogContent, CircularProgress, Box, Typography,
    IconButton, MenuItem, TextField, DialogActions, Tooltip, Grid, useMediaQuery, useTheme
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Autocomplete } from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from '@mui/material/Alert';

import { useAutocompleteData } from "../../utils/useAutocompleteData";
import { useComboData } from "../../utils/useComboData";
import { capitalizar } from "../../utils/capitalizar";
import { expedienteCertificadosJWT, crearCertificadoJWT, editarCertificadoJWT, eliminarCertificadoJWT, certificadoPdfJWT } from "../../api/endpoints";
import usePermisosModulo from "../../hooks/usePermisosModulo";
import { normalizeKeys } from "../../utils/normalizeKeys";


// Helpers de normalización

// Convierte a array: [], o Object.values(obj), o []
const toArray = (x) => Array.isArray(x) ? x : (x && typeof x === "object" ? Object.values(x) : []);

const norm = (s) =>
    String(s || "")
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // saca acentos
        .toLowerCase().replace(/\s+/g, " ").trim();

const repIdOf = (o) => o?.id ?? o?.Id ?? o?.ID ?? o?.Id_Reparticion ?? "";
const repNameOf = (o) => (o?.label ?? o?.nombre ?? o?.Nombre ?? o?.reparticion ?? o?.Reparticion ?? o?.descripcion ?? "").toString();


function mostrarMonto(valor) {
    const n = Number(String(valor).replace(",", "."));
    if (!isFinite(n)) return "–";
    return `$${n.toLocaleString("es-AR")}`;
}

function AlertSnackbar(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles((theme) => ({
    scrollX: { overflowX: "auto" },
    tableFull: { width: "100%", tableLayout: "fixed" },   // 👈 clave
    // quitamos el minWidth grande
    tableMin: { /* minWidth: 980,  <- eliminar esto */ width: "100%" },
    nowrap: { whiteSpace: "nowrap" },
    hideSm: { [theme.breakpoints.down('md')]: { display: "none" } },
    hideMd: { [theme.breakpoints.down('lg')]: { display: "none" } },
    actionsRow: { marginTop: theme.spacing(3), display: "flex", justifyContent: "flex-end", gap: 12 },

    dlgTitle: { background: "#5B7894", color: "#fff", borderTopLeftRadius: 15, borderTopRightRadius: 15, fontWeight: 700, fontSize: 22 },
    dlgContent: { background: "#fff", borderBottomLeftRadius: 15, borderBottomRightRadius: 15, padding: 18 },
    labelCell: { color: "#4b4b4b", fontWeight: 600 },

    // Montos en varias líneas
    montosCell: { whiteSpace: "normal", textAlign: "right" },
    montoLine: { fontWeight: 700 },
    montoSub: { fontSize: 12, opacity: 0.9 },
    montoSep: { padding: "0 6px" },

    // Bloques apilados (Plan / Cuenta / Exp Adm)
    cellBlock: {
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: 8,
        rowGap: 2,
    },
    cellTitle: { fontSize: 12, color: "#6b6b6b", whiteSpace: "nowrap" },

    planBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
    },
    planLines: {
        lineHeight: 1.25,
    },
    planIcon: {
        color: "#1769aa",
        marginLeft: 8,
    },
    // columnas (permitimos salto y elipsis si hace falta)
    colWrap: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        lineHeight: 1.15,
    },
    colEllipsis: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    colRight: { textAlign: "right" },

    clickRow: {
        cursor: "pointer",
        "&:hover": { background: "rgba(22,103,183,0.06)" },
    },

    
    // actions angosta
    actionsCol: { minWidth: 90, textAlign: "center" },
    actionsWrap: { display: "inline-flex", alignItems: "center", gap: 8 },

    chip: {
        marginLeft: 8,
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 6px",
        borderRadius: 4,
        background: "#E7F2FF",
        color: "#1769aa",
    },
}));


// Labels y máscaras dinámicas por módulo
const MOD_CONFIG = {
    1: { // Tributarias
        labelNumero: "Certificado",
        labelSerie: "Letra/Serie",
        serieMaxLength: 1,
        seriePattern: "[A-Za-z]",
        seriePlaceholder: "A",
        numeroMaxLength: 9,
        numeroInputMode: "numeric",
        numeroPattern: "[0-9]*",
        numeroPlaceholder: "123456789",
    },
    2: { // No Tributarias
        labelNumero: "Cert/NºRes",
        labelSerie: "Exp.Orig/NºSum",
        serieMaxLength: 20,
        seriePattern: undefined,
        seriePlaceholder: "",
        numeroMaxLength: 20,
        numeroInputMode: "text",
        numeroPattern: undefined,
        numeroPlaceholder: "",
    },
};
const MOD_DEFAULT = MOD_CONFIG[1]; // fallback a Tributarias

export default function TabCertificados({ idExpediente, idModulo, modulo }) {

    const modCfg = MOD_CONFIG[idModulo] || MOD_DEFAULT;

    const [certificados, setCertificados] = useState([]);
    const [loading, setLoading] = useState(true);

    const [openForm, setOpenForm] = useState(false);
    const [formData, setFormData] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    // --- HISTORIAL (anteriores) ---
    const [historial, setHistorial] = useState([]);
    const fetchHistorial = useCallback(async () => {
        // Backend endpoint for historial not yet implemented
        setHistorial([]);
    }, []);

    // ---------------- Permisos por módulo ----------------
    const { permDe, ready: permReady } = usePermisosModulo(modulo);
    const permCert = permDe("PESTAÑA CERTIFICADOS", "CERTIFICADOS");
    const perm = { ready: permReady, ...permCert };

    const [impuestosLocal] = useState([]);

    // ---------------- Datos ----------------
    const fetchCertificados = useCallback(async () => {
        if (perm.ready && !perm.select) { setLoading(false); return; }
        setLoading(true);
        try {
            const data = await expedienteCertificadosJWT(idExpediente);
            const raw = data?.certificados || [];
            const certs = normalizeKeys(raw);
            setCertificados(Array.isArray(certs) ? certs : []);
        } catch (e) {
            console.error('[CertificadosTab] Error en fetch:', e);
            setSnackbar({ open: true, message: "Error al cargar certificados.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [idExpediente, perm.ready, perm.select]);

    useEffect(() => {
        if (perm.ready && perm.select) {
            fetchCertificados();
            fetchHistorial();
        }
    }, [perm.ready, perm.select, fetchCertificados, fetchHistorial]);

    // ---------------- Autocomplete / Combos ----------------
    const {
        options: reparticiones,
        loading: loadingRepart,
        fetchOptions: fetchReparticiones
    } = useAutocompleteData("api_reparticiones.aspx");
    const { items: impuestos = [] } = useComboData("api_impuestos.aspx");
    const { items: estados = [] } = useComboData("api_estados_planes_pago.aspx");

    // impuestos loading: useComboData("api_impuestos.aspx") above auto-maps to JWT
    // No additional direct fetch needed

    // ---------------- Detalle Plan de Pago ----------------
    const [openDetalle, setOpenDetalle] = useState(false);
    const [detallePlan, setDetallePlan] = useState(null);
    const [detalleLoading, setDetalleLoading] = useState(false);

    // ---------------- Form ----------------
    const [pendingCertToEdit, setPendingCertToEdit] = useState(null);

    const handleOpenForm = (cert = null) => {
        // 1) si viene un cert, prepará la opción sombra (id + nombre visibles en el campo)
        if (cert) {
            const shadow = {
                id: String(cert.id_reparticion || ""),
                nombre: String(cert.reparticion || ""),
            };
            // si no existe en options actuales, la usamos; igual pedimos fetch por nombre
            const exists = reparticiones.some(
                opt => String(repIdOf(opt)) === String(shadow.id)
                    || norm(repNameOf(opt)) === norm(shadow.nombre)
            );
            setShadowRep(!exists && (shadow.id || shadow.nombre) ? shadow : null);

            // intentar traer por nombre para que aparezca la real
            if (shadow.nombre) fetchReparticiones(shadow.nombre);
        } else {
            setShadowRep(null);
        }

        // 2) setear formData y abrir SIEMPRE
        setFormData(
            cert
                ? {
                    ...cert,
                    id_reparticion: String(cert.id_reparticion || ""),
                    impuesto: String(cert.impuesto || ""),
                    plan_pago_estado: String(cert.plan_pago_estado || ""),
                    id_expediente: String(idExpediente || cert.id_expediente || ""),
                }
                : {
                    numero: "",
                    serie: "",
                    id_reparticion: "",
                    impuesto: "",
                    monto: "",
                    cuenta: "",
                    exp_administrativo: "",
                    plan_pago_numero: "",
                    plan_pago_estado: "",
                    neto: "",
                    intereses: "",
                    id_expediente: String(idExpediente || ""),
                }
        );
        setEditMode(!!cert);
        setOpenForm(true);
    };

    useEffect(() => {
        if (pendingCertToEdit && reparticiones.length) {
            const c = pendingCertToEdit;
            const found = reparticiones.find(opt =>
                String(repIdOf(opt)) === String(c.id_reparticion) ||
                norm(repNameOf(opt)) === norm(c.reparticion)
            );
            if (found) {
                setPendingCertToEdit(null);
                handleOpenForm(c);
            }
        }
        // eslint-disable-next-line
    }, [reparticiones]);

    const handleCloseForm = () => {
        setOpenForm(false);
        setFormData({});
    };

    const handleChange = (eOrName, maybeValue) => {
        if (typeof eOrName === "string") {
            const name = eOrName, value = maybeValue;
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            const { name, value } = eOrName.target;
            let val = value;
            // Módulo 1: serie auto-uppercase (letra), número solo dígitos
            if (idModulo === 1) {
                if (name === "serie") val = val.toUpperCase().replace(/[^A-Z]/g, '');
                if (name === "numero") val = val.replace(/[^0-9]/g, '');
            }
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    // --- DERIVADOS / MAPEOS (antes de handleOpenForm) ---
    const repArr = toArray(reparticiones);

    const [shadowRep, setShadowRep] = useState(null);

    const repOptions = useMemo(() => {
        if (!shadowRep) return repArr;
        const has = repArr.some(o => String(repIdOf(o)) === String(shadowRep.id));
        return has ? repArr : [shadowRep, ...repArr];
    }, [repArr, shadowRep]);

    useEffect(() => {
        if (shadowRep && repArr.some(o => String(repIdOf(o)) === String(shadowRep.id))) {
            setShadowRep(null);
        }
    }, [repArr, shadowRep]);


    // Fuente de impuestos: hook primero; si viene vacío, usamos la carga local
    const impuestosSource = (toArray(impuestos).length ? toArray(impuestos) : toArray(impuestosLocal));

    // Opciones desde API → value = código (ej. "AU"), label = "Id - Código"
    const impArrFromApi = impuestosSource.map((i) => {
        const codigo = String(i?.label ?? i?.nombre ?? i?.Impuesto ?? i?.codigo ?? "").trim();
        const idNum = String(i?.id ?? i?.Id ?? "").trim();
        return {
            value: codigo,                     // 👈 lo que guardamos sigue siendo el código
            label: `${idNum} - ${capitalizar(codigo)}`,  // 👈 lo que mostramos
        };
    });

    // Fallback desde la grilla → asegura que AU (u otros códigos) esté presente
    const impArrFromGrid = Array.from(new Set((certificados || [])
        .map(c => c?.impuesto)
        .filter(Boolean)))
        .map(v => ({ value: String(v), label: capitalizar(String(v)) }));

    // Merge (sin duplicados por value)
    const impOptions = useMemo(() => {
        const map = new Map();
        [...impArrFromApi, ...impArrFromGrid].forEach(o => {
            if (!map.has(o.value)) map.set(o.value, o);
        });
        return Array.from(map.values());
    }, [impArrFromApi, impArrFromGrid]);

    // Estados
    const estArr = useMemo(() => {
        return toArray(estados).map(o => ({
            value: String(o?.id ?? o?.Id ?? o?.value ?? o ?? ""),
            label: String(o?.label ?? o?.nombre ?? o?.Nombre ?? o?.estado ?? o?.Estado ?? o ?? ""),
        }));
    }, [estados]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editMode && !perm.update) return;
        if (!editMode && !perm.insert) return;

        // Validación Plan Pago: si estado es 1 o 2, el número es obligatorio
        const ppEst = String(formData.plan_pago_estado || "");
        if ((ppEst === "1" || ppEst === "2") && !formData.plan_pago_numero?.trim()) {
            setSnackbar({ open: true, message: "Debe ingresar el Nº de Plan de Pago cuando el estado lo requiere", severity: "warning" });
            return;
        }

        setFormLoading(true);
        try {
            let resp;
            if (editMode && formData.id) {
                resp = await editarCertificadoJWT(idExpediente, formData.id, formData);
            } else {
                resp = await crearCertificadoJWT(idExpediente, formData);
            }

            if (resp?.ok) {
                setOpenForm(false);
                const msg = resp.asignado
                    ? "Certificado existente sin expediente fue asignado a este proceso"
                    : (editMode ? "Certificado actualizado" : "Certificado creado");
                setSnackbar({ open: true, message: msg, severity: resp.asignado ? "info" : "success" });
                await Promise.all([fetchCertificados(), fetchHistorial()]);
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || "Error al guardar certificado", severity: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err?.message || "Error al guardar.", severity: "error" });
        } finally {
            setFormLoading(false);
        }
    };

    const handleCloseDetalle = () => {
        setOpenDetalle(false);
        setDetallePlan(null);
    };

    // ---------------- Delete ----------------
    const [openConfirm, setOpenConfirm] = useState(false);
    const [certToDelete, setCertToDelete] = useState(null);

    const handleAskDelete = (cert) => {
        if (!perm.delete) return;
        setCertToDelete(cert);
        setOpenConfirm(true);
    };

    const handleConfirmDelete = async () => {
        setOpenConfirm(false);
        if (!certToDelete?.id) return;
        setDeleting(certToDelete.id);
        try {
            const resp = await eliminarCertificadoJWT(idExpediente, certToDelete.id);
            if (resp?.ok) {
                setSnackbar({ open: true, message: "Certificado eliminado", severity: "success" });
                await Promise.all([fetchCertificados(), fetchHistorial()]);
            } else {
                setSnackbar({ open: true, message: resp?.mensaje || "Error al eliminar", severity: "error" });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err?.message || "Error al eliminar certificado", severity: "error" });
        } finally {
            setDeleting(null);
            setCertToDelete(null);
        }
    };

    // ------------- PDF handler -------------
    const [pdfLoading, setPdfLoading] = useState(null);
    const handleVerPdf = async (cert, e) => {
        if (e) e.stopPropagation();
        setPdfLoading(cert.id);
        try {
            const blobUrl = await certificadoPdfJWT(idExpediente, cert.id);
            if (blobUrl) {
                window.open(blobUrl, '_blank');
            } else {
                setSnackbar({ open: true, message: "No hay PDF disponible para este certificado.", severity: "info" });
            }
        } catch {
            setSnackbar({ open: true, message: "Error al obtener el PDF.", severity: "error" });
        } finally {
            setPdfLoading(null);
        }
    };

    const classes = useStyles();
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down('md'));

    // state para detalle
    const [selectedCert, setSelectedCert] = useState(null);

    // Click en fila: si hay PDF del certificado, abrirlo en nueva pestaña. Si no, mostrar modal de datos.
    const handleRowClick = async (cert) => {
        setPdfLoading(cert.id);
        try {
            const blobUrl = await certificadoPdfJWT(idExpediente, cert.id);
            if (blobUrl) {
                window.open(blobUrl, '_blank');
                return;
            }
        } catch { /* continuamos al fallback */ }
        finally { setPdfLoading(null); }

        // Fallback: sin PDF → mostrar modal con datos del certificado
        setSelectedCert(cert);
        setDetalleLoading(true);
        setOpenDetalle(true);
        setDetallePlan(null);
        setDetalleLoading(false);
    };

    const handlePrintDetalle = () => {
        if (!selectedCert) return;

        const P = {
            reparticion: selectedCert.reparticion || "—",
            impuesto: selectedCert.impuesto || "—",
            certificado: selectedCert.serie ? `${selectedCert.serie}-${selectedCert.numero}` : (selectedCert.numero || "—"),
            monto: mostrarMonto(selectedCert.monto),
            neto: mostrarMonto(selectedCert.neto),
            intereses: mostrarMonto(selectedCert.intereses),
            vigente: mostrarMonto(selectedCert.monto_vigente),
            cuenta: selectedCert.cuenta || "—",
            expAdm: selectedCert.exp_administrativo || "—",
            plan: (detallePlan?.plan_pago_numero || selectedCert.plan_pago_numero || "—"),
            estado: capitalizar(detallePlan?.estado || selectedCert.plan_pago_estado || "—"),
            fechaAct: selectedCert.fecha_actualizacion || null,
        };

        const filas = [
            ["Repartición", capitalizar(P.reparticion)],
            ["Impuesto", String(P.impuesto || "—").toUpperCase()],
            [modCfg.labelNumero, P.certificado],
            ["Monto", P.monto],
            ["Neto / Intereses", `${P.neto} / ${P.intereses}`],
            ["Monto vigente", P.vigente],
            ["Plan Pago Nº", P.plan],
            ["Estado Plan", P.estado],
            ["Cuenta", P.cuenta],
            ["Exp. Administrativo", P.expAdm],
            ...(P.fechaAct ? [["Fecha actualización", P.fechaAct]] : []),
        ];

        const rowsHtml = filas.map(([l, v]) => `
            <div class="row">
              <div class="label">${l}</div>
              <div class="value">${v}</div>
            </div>
          `).join("");

        const html = `
            <!doctype html>
            <html>
              <head>
                <meta charset="utf-8"/>
                <title>Certificado</title>
                <style>
                  @page{ margin: 16mm; }
                  *{ box-sizing:border-box; }
                  body{ font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif; color:#222; font-size:12.5pt; margin:0; padding:0; }
                  .header{ display:flex; justify-content:space-between; align-items:center; font-size:10pt; color:#666; margin-bottom:6mm; }
                  .title{ font-size:18pt; font-weight:700; color:#2b3a47; margin:0 0 2mm 0; }
                  .sub{ color:#6b6b6b; font-size:11pt; margin-bottom:6mm; }
                  .card{ border:1px solid #e6edf3; border-radius:10px; padding:12px 14px; box-shadow:0 1px 3px rgba(0,0,0,0.04); }
                  .row{ display:grid; grid-template-columns: 38% 62%; gap:6px 16px; padding:6px 0; border-bottom:1px solid #f0f4f7; }
                  .row:last-child{ border-bottom:none; }
                  .label{ color:#566; font-weight:600; }
                  .value{}
                  .muted{ color:#666; }
                </style>
              </head>
              <body>
                <div class="header">
                  <div>${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
                  <div class="muted">Certificado</div>
                </div>
                <h1 class="title">${capitalizar(P.reparticion)}</h1>
                <div class="sub">Impuesto: <b>${String(P.impuesto).toUpperCase()}</b></div>
                <div class="card">${rowsHtml}</div>
              </body>
            </html>
          `;

        // ---- imprimir en iframe oculto (sin pestaña visible)
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document || iframe.contentDocument;
        if (!doc) return;
        doc.open(); doc.write(html); doc.close();

        setTimeout(() => {
            const w = iframe.contentWindow;
            w.focus();
            w.print();
            setTimeout(() => document.body.removeChild(iframe), 250);
        }, 100);
    };


    return (
        <Box p={2} className={classes.root}>
            {/* Encabezado + Botón Agregar */}
            <Box display="flex" alignItems="center" mb={2}>
                <Box flexGrow={1} />
                <Tooltip title={perm.insert ? "Agregar certificado" : "No tenés permiso para agregar"}>
                    <span>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!perm.insert}
                            style={{
                                borderRadius: 4,
                                background: perm.insert ? "#1769aa" : undefined,
                                fontWeight: 600,
                                textTransform: "none",
                                marginBottom: 8,
                                color: "#fff",
                            }}
                            onClick={() => handleOpenForm()}
                        >
                            + Agregar Certificado
                        </Button>
                    </span>
                </Tooltip>
            </Box>
            {/* Aviso sin permisos de lectura */}
            {perm.ready && !perm.select && (
                <Paper style={{ padding: 16, borderRadius: 4 }}>
                    <Typography color="text.secondary">
                        No tenés permisos para ver Certificados (SELECT).
                    </Typography>
                </Paper>
            )}
            {/* Tabla */}
            {perm.ready && perm.select && (
                loading ? (
                    <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                ) : certificados.length === 0 ? (
                    <Paper style={{ padding: 16, borderRadius: 4 }}>
                        <Typography color="text.secondary">No hay certificados cargados.</Typography>
                    </Paper>
                ) : (
                    <>
                        {/* Tabla del certificado vigente */}
                        <TableContainer component={Paper} className={classes.scrollX}
                            style={{ width: "100%", borderRadius: 4, boxShadow: "0px 2px 8px rgba(0,0,0,0.09)" }}>
                                    <Table className={`${classes.tableFull}`} size={isSmDown ? "small" : "medium"}>
                                        <colgroup>
                                            <col style={{ width: "27%" }} />
                                            <col style={{ width: "12%" }} />
                                            <col style={{ width: "17%" }} />
                                            <col style={{ width: "30%" }} />
                                            <col style={{ width: "14%" }} />
                                        </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Repartición / Impuesto</TableCell>
                                                <TableCell>{modCfg.labelNumero}</TableCell>
                                                <TableCell align="right">Montos</TableCell>
                                                <TableCell className={classes.hideSm}>Plan / Cuenta / Exp. Adm.</TableCell>
                                                <TableCell align="center" className={classes.actionsCol}>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {certificados.map((cert, idx) => (
                                                <TableRow
                                                    key={cert.id}
                                                    className={classes.clickRow}
                                                    onClick={() => handleRowClick(cert)}   // tu handler para abrir detalle
                                                    hover
                                                    style={{ height: isSmDown ? 44 : 52 }}
                                                    sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}
                                                >
                                                    {/* 1) Repartición / Impuesto */}
                                                    <TableCell className={classes.colWrap}>
                                                        <div style={{ fontWeight: 600 }}>{capitalizar(cert.reparticion)}</div>
                                                        <div style={{ fontSize: 12, opacity: .8 }}>
                                                            Impuesto: {capitalizar(cert.impuesto)}
                                                        </div>
                                                    </TableCell>

                                                    {/* 2) Certificado */}
                                                    <TableCell className={classes.colEllipsis}>
                                                        {cert.serie ? `${cert.serie}-` : ""}{cert.numero}
                                                    </TableCell>

                                                    {/* 3) Montos */}
                                                    <TableCell className={`${classes.montosCell} ${classes.colRight}`}>
                                                        <div className={classes.montoLine}>{mostrarMonto(cert.monto)}</div>
                                                        <div className={classes.montoSub}>
                                                            Neto: {mostrarMonto(cert.neto)} · Int: {mostrarMonto(cert.intereses)}<br />
                                                            Vigente: {mostrarMonto(cert.monto_vigente)}
                                                        </div>
                                                    </TableCell>

                                                    {/* 4) Plan / Cuenta / Exp. Adm. */}
                                                    <TableCell className={classes.colWrap}>
                                                        <div><b>Plan</b> {cert.plan_pago_numero || "—"}</div>
                                                        <div><b>Cuenta</b> {cert.cuenta || "—"}</div>
                                                        <div><b>Exp. Adm.</b> {cert.exp_administrativo || "—"}</div>
                                                    </TableCell>

                                                    {/* Acciones */}
                                                    <TableCell
                                                        align="center"
                                                        className={classes.actionsCol}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <span className={classes.actionsWrap}>
                                                            {cert.tiene_pdf === 1 && (
                                                            <Tooltip title="Ver PDF" arrow>
                                                                <span>
                                                                    <IconButton
                                                                        onClick={(e) => handleVerPdf(cert, e)}
                                                                        size="small"
                                                                        style={{ color: "#d32f2f" }}
                                                                        disabled={pdfLoading === cert.id}
                                                                    >
                                                                        {pdfLoading === cert.id
                                                                            ? <CircularProgress size={18} color="inherit" />
                                                                            : <PictureAsPdfIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            )}

                                                            <Tooltip title={perm.update ? "Editar" : "No tenés permiso para editar"} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => perm.update && handleOpenForm(cert)}
                                                                        size="small"
                                                                        style={{ color: perm.update ? "#1769aa" : undefined, opacity: perm.update ? 1 : 0.4 }}
                                                                        disabled={!perm.update}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>

                                                            <Tooltip title={perm.delete ? "Eliminar" : "No tenés permiso para eliminar"} arrow>
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => perm.delete && handleAskDelete(cert)}
                                                                        size="small"
                                                                        style={{ color: "#F22E44", opacity: perm.delete ? 1 : 0.4 }}
                                                                        disabled={!perm.delete || deleting === cert.id}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </span>
                                                    </TableCell>

                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                            </TableContainer>
                        {/* Historial: anteriores (informativo) */}
                        {historial.length > 0 && (
                            <Box mt={3}>
                                <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#4b4b4b", marginBottom: 8 }}>
                                    Certificados anteriores (informativo)
                                </Typography>

                                <TableContainer component={Paper} className={classes.scrollX}
                                    style={{ borderRadius: 4, boxShadow: "0px 2px 8px rgba(0,0,0,0.09)", width: "100%" }}>
                                            <Table
                                                className={classes.tableMin}
                                                size={isSmDown ? "small" : "medium"}
                                                style={{ tableLayout: "fixed" }}      // 👈 ancho fijo
                                            >
                                                {/* 👇 define anchos relativos a tu layout de 5 columnas */}
                                                <colgroup>
                                                    <col style={{ width: "35%" }} />
                                                    <col style={{ width: "11%" }} />
                                                    <col style={{ width: "17%" }} />
                                                    <col style={{ width: "25%" }} />
                                                    <col style={{ width: "11%" }} />
                                                </colgroup>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.nowrap}>Fecha actualización</TableCell>
                                                <TableCell className={classes.nowrap}>Repartición</TableCell>
                                                <TableCell className={classes.nowrap}>Impuesto</TableCell>
                                                <TableCell className={classes.nowrap}>Certificado</TableCell>
                                                <TableCell className={classes.nowrap}>Letra/Serie</TableCell>
                                                <TableCell align="right" className={classes.nowrap}>Monto</TableCell>
                                                <TableCell align="right" className={`${classes.nowrap} ${classes.hideSm}`}>Neto</TableCell>
                                                <TableCell align="right" className={`${classes.nowrap} ${classes.hideSm}`}>Intereses</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {historial.map((h, idx) => (
                                                <TableRow key={h.id} hover style={{ height: isSmDown ? 44 : 52 }} sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                    <TableCell className={classes.nowrap}>{h.fecha_actualizacion || "–"}</TableCell>
                                                    <TableCell className={classes.nowrap}>{capitalizar(h.reparticion || "")}</TableCell>
                                                    <TableCell className={classes.nowrap}>{capitalizar(h.impuesto || "")}</TableCell>
                                                    <TableCell className={classes.nowrap}>{h.numero || "–"}</TableCell>
                                                    <TableCell className={classes.nowrap}>{h.serie || "–"}</TableCell>
                                                    <TableCell align="right" className={classes.nowrap}>{mostrarMonto(h.monto)}</TableCell>
                                                    <TableCell align="right" className={`${classes.nowrap} ${classes.hideSm}`}>{mostrarMonto(h.neto)}</TableCell>
                                                    <TableCell align="right" className={`${classes.nowrap} ${classes.hideSm}`}>{mostrarMonto(h.intereses)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </>
                )
            )}
            {/* Confirmación de borrado */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle style={{ background: "#6B89AC", color: "#fff" }}>
                    Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" style={{ margin: "18px 0 6px 0" }}>
                        ¿Seguro que desea eliminar este certificado?
                    </Typography>
                    <Box mt={1}>
                           <Typography variant="body2" color="text.secondary">
                                 <b>Repartición:</b> {capitalizar(certToDelete?.reparticion || "")}
                               </Typography>
                           <Typography variant="body2" color="text.secondary">
                                 <b>Certificado:</b> {`${certToDelete?.serie || ""}${certToDelete?.numero ? `-${certToDelete.numero}` : ""}`}
                               </Typography>
                           <Typography variant="body2" color="text.secondary">
                                 <b>Impuesto:</b> {certToDelete?.impuesto ?? ""}
                               </Typography>
                           <Typography variant="body2" color="text.secondary">
                                 <b>Monto:</b> {mostrarMonto(certToDelete?.monto)}
                               </Typography>
                         </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)} variant="outlined" style={{ borderColor: "#F22E44", color: "#F22E44" }}>
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" style={{ background: "#1769aa", color: "#fff", marginLeft: 12 }}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal alta/edición */}
            <Dialog open={openForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
                <DialogTitle
                    style={{
                        background: "#5B7894",
                        color: "white",
                        borderTopLeftRadius: 15,
                        borderTopRightRadius: 15,
                        fontWeight: 700,
                        fontSize: 22,
                    }}
                >
                    {editMode ? "Editar Certificado" : "Nuevo Certificado"}
                </DialogTitle>
                <DialogContent className={classes.dlgContent} style={{ background: "#fff", borderBottomLeftRadius: 15, borderBottomRightRadius: 15, padding: 18 }}>
                    <form id="certForm" onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Autocomplete
                                    options={repOptions}
                                    getOptionLabel={(option) => capitalizar(repNameOf(option) || option?.nombre || "")}
                                    isOptionEqualToValue={(o, v) => String(repIdOf(o)) === String(repIdOf(v) || v)}
                                    value={
                                        repOptions.find(o => String(repIdOf(o)) === String(formData.id_reparticion)) || null
                                    }
                                    onChange={(e, value) => {
                                        setFormData(prev => ({ ...prev, id_reparticion: value ? value.id : "" }));
                                        if (value && shadowRep && String(value.id) === String(shadowRep.id)) {
                                            setShadowRep(null);
                                        }
                                    }}
                                    onInputChange={(e, value) => fetchReparticiones(value || "")}
                                    loading={loadingRepart}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth label="Repartición" required variant="outlined" />
                                    )}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Impuesto"
                                    name="impuesto"
                                    required
                                    variant="outlined"
                                    value={formData.impuesto || ""}
                                    onChange={handleChange}             
                                    InputLabelProps={{ shrink: true }}
                                    SelectProps={{ displayEmpty: true }}
                                    helperText="Seleccione el impuesto asociado al certificado"
                                >
                                    {impOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField fullWidth label={modCfg.labelNumero} name="numero" required variant="outlined"
                                    value={formData.numero || ""} onChange={handleChange}
                                    inputProps={{
                                        maxLength: modCfg.numeroMaxLength,
                                        inputMode: modCfg.numeroInputMode,
                                        ...(modCfg.numeroPattern ? { pattern: modCfg.numeroPattern } : {}),
                                    }}
                                    placeholder={modCfg.numeroPlaceholder}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField fullWidth label={modCfg.labelSerie} name="serie" variant="outlined"
                                    value={formData.serie || ""} onChange={handleChange}
                                    inputProps={{
                                        maxLength: modCfg.serieMaxLength,
                                        ...(modCfg.seriePattern ? { pattern: modCfg.seriePattern } : {}),
                                    }}
                                    placeholder={modCfg.seriePlaceholder}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField fullWidth label="Monto" name="monto" required type="number" variant="outlined"
                                    value={formData.monto || ""} onChange={handleChange}
                                    InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }} className={classes.hideSm}>
                                <TextField fullWidth label="Neto" name="neto" type="number" variant="outlined"
                                    value={formData.neto || ""} onChange={handleChange}
                                    InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }} className={classes.hideSm}>
                                <TextField fullWidth label="Intereses" name="intereses" type="number" variant="outlined"
                                    value={formData.intereses || ""} onChange={handleChange}
                                    InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }} className={classes.hideSm}>
                                <TextField fullWidth label="Cuenta" name="cuenta" variant="outlined"
                                    value={formData.cuenta || ""} onChange={handleChange} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField fullWidth label="Exp. Administrativo" name="exp_administrativo" variant="outlined"
                                    value={formData.exp_administrativo || ""} onChange={handleChange} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }} className={classes.hideSm}>
                                <TextField fullWidth label="Plan Pago Nº" name="plan_pago_numero" variant="outlined"
                                    value={formData.plan_pago_numero || ""} onChange={handleChange} />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }} className={classes.hideSm}>
                                <TextField
                                    select fullWidth label="Estado Plan" name="plan_pago_estado" variant="outlined"
                                    value={formData.plan_pago_estado || ""} onChange={handleChange}
                                >
                                    {estArr.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{capitalizar(opt.label)}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </form>

                </DialogContent>
                <DialogActions style={{ justifyContent: "flex-end", padding: "12px 20px" }}>
                    <Button variant="outlined" style={{ borderColor: "#F22E44", color: "#F22E44" }} onClick={handleCloseForm}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        form="certForm"
                        variant="contained"
                        style={{ background: "#1769aa", color: "#fff", marginLeft: 12 }}
                        disabled={formLoading || (editMode ? !perm.update : !perm.insert)}
                    >
                        {formLoading ? <CircularProgress size={22} color="inherit" /> : (editMode ? "Guardar" : "Crear")}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal Detalle Plan de Pago */}
            <Dialog open={openDetalle} onClose={handleCloseDetalle} maxWidth="sm" fullWidth>
                <DialogTitle className={classes.dlgTitle}>
                    Detalle del Certificado
                </DialogTitle>

                <DialogContent className={classes.dlgContent}>
                    {detalleLoading ? (
                        <CircularProgress />
                    ) : selectedCert ? (
                        detallePlan?.error ? (
                            <Typography color="error">{detallePlan.error}</Typography>
                        ) : (
                            <div id="print-cert">
                                <Box mb={1.5}>
                                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                                        {capitalizar(selectedCert.reparticion)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Impuesto: {capitalizar(selectedCert.impuesto || "—")}
                                    </Typography>
                                </Box>

                                <Grid container spacing={1}>
                                    <Grid size={6} className={classes.labelCell}><Typography>{modCfg.labelNumero}</Typography></Grid>
                                    <Grid size={6}><Typography>{selectedCert.serie ? `${selectedCert.serie}-${selectedCert.numero}` : (selectedCert.numero || "—")}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Monto</Typography></Grid>
                                    <Grid size={6}><Typography>{mostrarMonto(selectedCert.monto)}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Neto / Intereses</Typography></Grid>
                                    <Grid size={6}><Typography>{`${mostrarMonto(selectedCert.neto)} / ${mostrarMonto(selectedCert.intereses)}`}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Monto vigente</Typography></Grid>
                                    <Grid size={6}><Typography>{mostrarMonto(selectedCert.monto_vigente)}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Cuenta</Typography></Grid>
                                    <Grid size={6}><Typography>{selectedCert.cuenta || "—"}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Exp. Administrativo</Typography></Grid>
                                    <Grid size={6}><Typography>{selectedCert.exp_administrativo || "—"}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Plan Pago Nº</Typography></Grid>
                                    <Grid size={6}><Typography>{(detallePlan?.plan_pago_numero || selectedCert.plan_pago_numero || "—")}</Typography></Grid>

                                    <Grid size={6} className={classes.labelCell}><Typography>Estado Plan</Typography></Grid>
                                    <Grid size={6}><Typography>{capitalizar(detallePlan?.estado || selectedCert.plan_pago_estado || "—")}</Typography></Grid>

                                    {selectedCert.fecha_actualizacion && (
                                        <>
                                            <Grid size={6} className={classes.labelCell}><Typography>Fecha actualización</Typography></Grid>
                                            <Grid size={6}><Typography>{selectedCert.fecha_actualizacion}</Typography></Grid>
                                        </>
                                    )}
                                </Grid>
                            </div>
                        )
                    ) : (
                        <Typography color="text.secondary">No se encontró información.</Typography>
                    )}
                </DialogContent>

                <DialogActions style={{ justifyContent: "space-between", padding: "12px 20px" }}>
                    <Box>
                        <Button
                            onClick={handlePrintDetalle}
                            variant="outlined"
                            style={{ borderColor: "#1769aa", color: "#1769aa" }}
                        >
                            Imprimir
                        </Button>
                    </Box>

                    <Box>
                        <Button variant="outlined" style={{ borderColor: "#F22E44", color: "#F22E44" }} onClick={handleCloseDetalle}>
                            Cerrar
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
                <AlertSnackbar severity={snackbar.severity}>{snackbar.message}</AlertSnackbar>
            </Snackbar>
        </Box>
    );
}
